import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faAddressBook,
  faChurch,
  faMobileScreen,
  faUserShield,
  faChartPie,
  faLayerGroup,
  faEye,
  faHeart,
  faClipboardCheck,
  faComments,
  faPeopleGroup,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
  "../images/react.png",
  "../images/azure.svg",
  "../images/azure-sql.png",
  "../images/nodejs.png",
  "../images/csharp.png",
];

const MyChurchDirectory = () => {
  return (
    <>
      <Helmet>
        <title>My Church Directory Case Study | Church Directory Software by Data Repo</title>
        <meta
          name="description"
          content="See how Data Repo developed My Church Directory, a church member directory software that helps churches manage member information, improve communication, and strengthen their community."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
        <h1 className="text-3xl md:text-6xl font-bold uppercase">My Church Directory</h1>
        <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
          Church Member Directory Software Developed by Data Repo
        </p>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Helping Churches Stay Connected</h2>
            <p className="text-lg text-gray-700 mb-4">
              Churches often rely on spreadsheets or paper records to manage member information,
              which makes communication and coordination difficult.
            </p>
            <p className="text-lg text-gray-700">
              My Church Directory was built by Data Repo to provide a simple, secure system
              that helps churches manage member data, improve communication, and strengthen their community.
            </p>
          </div>
          <img src="../images/my-church-directory-churches.png" alt="Church directory dashboard" className="rounded-xl shadow-lg" />
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Inside the My Church Directory Dashboard</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            My Church Directory provides church administrators with a simple interface to manage
            members, families, communication, and church activities in one place.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/my-church-directory-subgroups.png" className="rounded-xl shadow-lg"  alt="church directory" />
          <img src="../images/my-church-directory-family.png" className="rounded-xl shadow-lg" alt="church directory" />
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
              <p className="text-lg font-semibold text-gray-800">
                My Church Directory is actively being used by churches
              </p>
            </div>
          </div>

          <a
            href="https://mycdir.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
          >
            Visit Website →
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            The goal was to create a secure and easy-to-use church directory system
            that improves member communication and church organization.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faAddressBook} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Centralized Member Records</h3>
              <p>Store and manage church member contact information in one place.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Improved Visibility</h3>
              <p>Give church leaders clear insight into their congregation.</p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">Scalable Platform</h3>
              <p>Support churches of all sizes with a flexible system.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="../images/church-manual.jpg" alt="Manual church records" className="rounded-xl shadow-md" />
          <div>
            <h2 className="text-3xl font-bold mb-6">Challenges Churches Faced</h2>
            <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
              <li>Member data stored in scattered spreadsheets</li>
              <li>Difficulty keeping contact details updated</li>
              <li>Limited communication between church groups</li>
              <li>No centralized system for ministries and activities</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <FontAwesomeIcon icon={faUsers} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Member Directory</h3>
            <p>Searchable member profiles with family and group associations.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChurch} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Ministry Management</h3>
            <p>Organize members into ministries and church groups.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faMobileScreen} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Mobile Access</h3>
            <p>Access the church directory from phones, tablets, or desktops.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faUserShield} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Secure Access</h3>
            <p>Role-based permissions to protect member privacy.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChartPie} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Reports & Insights</h3>
            <p>Generate reports to understand engagement and participation.</p>
          </div>

          <div>
            <FontAwesomeIcon icon={faHeart} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Community Connection</h3>
            <p>Strengthen communication and relationships within the church.</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            From member directories to attendance and communication tools,
            the platform simplifies church administration.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img src="../images/my-church-directory-events.png" className="rounded-xl shadow-lg" alt="church directory" />
          <img src="../images/my-church-directory-user-roles.png" className="rounded-xl shadow-lg" alt="church directory" />
          <img src="../images/church-directory-family-members.png" className="rounded-xl shadow-lg" alt="church directory" />
          <img src="../images/church-directory-support-tickets.png" className="rounded-xl shadow-lg" alt="church directory" />
        </div>
      </section>

      <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Community Impact</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            After adopting My Church Directory, churches experienced stronger communication,
            improved organization, and better engagement across their congregation.
          </p>

          <div className="grid md:grid-cols-3 gap-10">

            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-blue-100 text-blue-700 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faComments} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Improved Communication</h3>
              <p className="text-gray-600">
                Members can easily find contact information, making church-wide and group communication more effective.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-green-100 text-green-700 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faPeopleGroup} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Stronger Community Engagement</h3>
              <p className="text-gray-600">
                Ministries and church groups stay better connected, encouraging participation and involvement.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-yellow-100 text-yellow-700 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faClipboardCheck} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">Simplified Administration</h3>
              <p className="text-gray-600">
                Church staff spend less time managing records and more time focusing on ministry and community support.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="flex flex-col items-center py-16 px-8">
        <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
        <div className="flex flex-wrap gap-8 justify-center">
          {technologiesIcon.map((item, index) => (
            <img key={index} src={item} alt="technology" width="90" />
          ))}
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default MyChurchDirectory